import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const LoginSignupPage: React.FC = () => {
  const { login, signup, isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isLogin, setIsLogin] = useState(true);
  // Whether public self-signup is open — driven by ALLOW_PUBLIC_REGISTRATION
  // in backend/.env. Defaults to hidden until the check resolves so a
  // disabled signup form never flashes into view.
  const [registrationEnabled, setRegistrationEnabled] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [gender, setGender] = useState('');
  const [country, setCountry] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);

  // Redirect authenticated users. A fitter who logged in without a specific
  // destination in mind lands on the portal, not the consumer recommendations page --
  // it is the only reason they have an account.
  useEffect(() => {
    if (isAuthenticated) {
      const requested = (location.state as any)?.from?.pathname;
      const home = user?.is_expert && !user?.is_superuser ? '/expert' : '/';
      navigate(requested || home, { replace: true });
    }
  }, [isAuthenticated, user, navigate, location]);

  useEffect(() => {
    axios
      .get(`${API_BASE_URL}/api/v1/auth/registration-status`)
      .then((res) => {
        const enabled = !!res.data?.enabled;
        setRegistrationEnabled(enabled);
        if (!enabled) setIsLogin(true);
      })
      .catch(() => setRegistrationEnabled(false));
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      if (isLogin) {
        await login(email, password, rememberMe);
      } else {
        if (!fullName.trim()) {
          setError('Full name is required');
          setIsLoading(false);
          return;
        }
        if (!dateOfBirth) {
          setError('Date of birth is required');
          setIsLoading(false);
          return;
        }
        if (!gender) {
          setError('Gender is required');
          setIsLoading(false);
          return;
        }
        if (!country) {
          setError('Country is required');
          setIsLoading(false);
          return;
        }
        await signup(email, password, fullName, { dateOfBirth, gender, country });
      }
    } catch (err: any) {
      setError(err.message || 'Authentication failed');
    } finally {
      setIsLoading(false);
    }
  };

  // Max date for date of birth = today, min = 100 years ago
  const today = new Date().toISOString().split('T')[0];
  const minDate = `${new Date().getFullYear() - 100}-01-01`;

  return (
    <div className="min-h-screen flex items-center justify-center bg-ground">
      <div className="max-w-md w-full mx-4 sm:mx-auto space-y-8 p-6 sm:p-8 bg-white rounded-xl shadow-overlay">
        {/* Logo */}
        <div className="flex flex-col items-center cursor-pointer gap-2" onClick={() => navigate('/')}>
          <img src="/dig_no_name_logo.png" alt="dialledin.golf" className="h-20 w-auto object-contain" />
          <img src="/dig_name_only_white_line.png" alt="dialledin.golf" className="h-10 w-auto object-contain" />
          <p className="text-ink-3 text-sm mt-1">A machine measures your swing; we recommend clubs built for it</p>
        </div>

        {/* Tab Switcher — Sign Up tab only appears when public registration is open */}
        {registrationEnabled && (
          <div className="flex border-b border-gray-200">
            <button
              type="button"
              onClick={() => {
                setIsLogin(true);
                setError('');
              }}
              className={`flex-1 py-3 px-4 text-center font-medium transition-colors ${
                isLogin
                  ? 'border-b-2 border-green-ink text-green-ink'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Login
            </button>
            <button
              type="button"
              onClick={() => {
                setIsLogin(false);
                setError('');
              }}
              className={`flex-1 py-3 px-4 text-center font-medium transition-colors ${
                !isLogin
                  ? 'border-b-2 border-green-ink text-green-ink'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Sign Up
            </button>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="mt-8 space-y-6">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
              {error}
            </div>
          )}

          {!isLogin && (
            <div>
              <label htmlFor="fullName" className="block text-sm font-medium text-gray-700 mb-2">
                Full Name *
              </label>
              <input
                id="fullName"
                type="text"
                required={!isLogin}
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-ink focus:border-transparent transition-all"
                placeholder="John Doe"
              />
            </div>
          )}

          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
              Email Address *
            </label>
            <input
              id="email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-ink focus:border-transparent transition-all"
              placeholder="you@example.com"
            />
          </div>

          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
              Password *
            </label>
            <input
              id="password"
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-ink focus:border-transparent transition-all"
              placeholder="••••••••"
            />
            {!isLogin && (
              <p className="mt-2 text-sm text-gray-500">
                Must be 8+ characters with uppercase, lowercase, and number
              </p>
            )}
          </div>

          {/* Keep me signed in — login mode only */}
          {isLogin && (
            <div className="flex items-center gap-3">
              <input
                id="rememberMe"
                type="checkbox"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                className="w-4 h-4 rounded border-gray-300 text-green-ink focus:ring-green-ink cursor-pointer"
              />
              <label htmlFor="rememberMe" className="text-sm text-gray-700 cursor-pointer select-none">
                Keep me signed in
              </label>
            </div>
          )}

          {isLogin && (
            <div className="text-right -mt-2">
              <button
                type="button"
                onClick={() => navigate('/forgot-password')}
                className="text-sm text-green-ink hover:text-green-ink font-medium"
              >
                Forgot password?
              </button>
            </div>
          )}

          {/* Demographics — signup only */}
          {!isLogin && (
            <>
              <div>
                <label htmlFor="dateOfBirth" className="block text-sm font-medium text-gray-700 mb-2">
                  Date of Birth *
                </label>
                <input
                  id="dateOfBirth"
                  type="date"
                  required={!isLogin}
                  value={dateOfBirth}
                  onChange={(e) => setDateOfBirth(e.target.value)}
                  max={today}
                  min={minDate}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-ink focus:border-transparent transition-all"
                />
                <p className="mt-1 text-sm text-gray-500">Used to tailor equipment recommendations to your age group</p>
              </div>

              <div>
                <label htmlFor="gender" className="block text-sm font-medium text-gray-700 mb-2">
                  Gender *
                </label>
                <select
                  id="gender"
                  required={!isLogin}
                  value={gender}
                  onChange={(e) => setGender(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-ink focus:border-transparent transition-all"
                >
                  <option value="">Select Gender</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                  <option value="Other">Other</option>
                  <option value="Prefer not to say">Prefer not to say</option>
                </select>
              </div>

              <div>
                <label htmlFor="country" className="block text-sm font-medium text-gray-700 mb-2">
                  Country *
                </label>
                <select
                  id="country"
                  required={!isLogin}
                  value={country}
                  onChange={(e) => setCountry(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-ink focus:border-transparent transition-all"
                >
                  <option value="">Select Country</option>
                  <option value="United States">United States</option>
                  <option value="Canada">Canada</option>
                  <option value="United Kingdom">United Kingdom</option>
                  <option value="Australia">Australia</option>
                  <option value="Ireland">Ireland</option>
                  <option value="Japan">Japan</option>
                  <option value="South Korea">South Korea</option>
                  <option value="Other">Other</option>
                </select>
              </div>
            </>
          )}

          <button
            type="submit"
            disabled={isLoading}
            className={`w-full py-3 px-4 text-white font-medium rounded-lg transition-colors ${
              isLoading
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-green-ink hover:opacity-90 active:bg-green-ink'
            }`}
          >
            {isLoading ? (
              <span className="flex items-center justify-center">
                <svg
                  className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                Processing...
              </span>
            ) : isLogin ? (
              'Login'
            ) : (
              'Create Account'
            )}
          </button>
        </form>

        {/* Footer */}
        <div className="text-center text-sm text-gray-600">
          {isLogin ? (
            registrationEnabled && (
              <p>
                New to Dialed In Golf?{' '}
                <button
                  type="button"
                  onClick={() => {
                    setIsLogin(false);
                    setError('');
                  }}
                  className="text-green-ink hover:text-green-ink font-medium"
                >
                  Create an account
                </button>
              </p>
            )
          ) : (
            <p>
              Already have an account?{' '}
              <button
                type="button"
                onClick={() => {
                  setIsLogin(true);
                  setError('');
                }}
                className="text-green-ink hover:text-green-ink font-medium"
              >
                Login here
              </button>
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default LoginSignupPage;
